import { useState, useRef, useEffect } from "react";
import { Wand2, Image, Video, Volume2, Languages, Loader2, RefreshCw, Play, Pause, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { AI_SAMPLES } from "@/lib/aiShowcaseSamples";

type Tab = "claude" | "images" | "video" | "voice" | "bilingual";
type AdCopy = { headline: string; body: string; cta: string };
type BilingualResult = { en: AdCopy; es: AdCopy };

interface ShowcaseMeta {
  adCopy: { en: AdCopy; es: AdCopy };
  images: string[];
  audioUrl: string;
  heygenVideoUrl: string;
  heygenVideoId: string;
  heygenStatus: "completed" | "processing";
  generatedAt: string;
}

// Showcase assets (Storage + edge functions) live on the functions project.
// VITE_SUPABASE_URL points to the main project; VITE_SHOWCASE_URL overrides for showcase.
const SHOWCASE_URL =
  (import.meta.env.VITE_SHOWCASE_URL as string | undefined) ||
  "https://yyoqtmbihanzxzeduimj.supabase.co";
const META_URL = `${SHOWCASE_URL}/storage/v1/object/public/showcase/meta.json`;

// Merge live Storage data over the static fallback
function mergeWithFallback(live: ShowcaseMeta | null): ShowcaseMeta {
  return {
    adCopy: live?.adCopy ?? AI_SAMPLES.adCopy,
    images: live?.images?.length ? live.images : AI_SAMPLES.images,
    audioUrl: live?.audioUrl ?? "",
    heygenVideoUrl: live?.heygenVideoUrl ?? AI_SAMPLES.heygenVideoUrl,
    heygenVideoId: live?.heygenVideoId ?? "",
    heygenStatus: live?.heygenStatus ?? "processing",
    generatedAt: live?.generatedAt ?? "",
  };
}

function useSamplesData() {
  const [data, setData] = useState<ShowcaseMeta>(mergeWithFallback(null));
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetch(`${META_URL}?t=${Date.now()}`) // bust cache
      .then((r) => (r.ok ? r.json() : null))
      .then((live) => setData(mergeWithFallback(live)))
      .catch(() => {/* keep fallback */})
      .finally(() => setFetching(false));
  }, []);

  async function refetchHeygenStatus() {
    if (!data.heygenVideoId) return;
    try {
      const res = await fetch(`${SHOWCASE_URL}/functions/v1/showcase-ad-copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "heygen-status", videoId: data.heygenVideoId }),
      });
      if (!res.ok) return;
      const result = await res.json() as { status?: string; video_url?: string };
      if (result.status === "completed" && result.video_url) {
        setData((prev) => ({ ...prev, heygenVideoUrl: result.video_url!, heygenStatus: "completed" }));
      }
    } catch { /* noop */ }
  }

  return { data, fetching, refetchHeygenStatus };
}

async function generateShowcaseAd(product: string, goal: string): Promise<BilingualResult> {
  const res = await fetch(`${SHOWCASE_URL}/functions/v1/showcase-ad-copy`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ product, goal }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

const TABS: { id: Tab; icon: React.ElementType; labelKey: string }[] = [
  { id: "claude",    icon: Wand2,     labelKey: "aiShowcase.tab.claude" },
  { id: "images",    icon: Image,     labelKey: "aiShowcase.tab.images" },
  { id: "video",     icon: Video,     labelKey: "aiShowcase.tab.video" },
  { id: "voice",     icon: Volume2,   labelKey: "aiShowcase.tab.voice" },
  { id: "bilingual", icon: Languages, labelKey: "aiShowcase.tab.bilingual" },
];

export function AiShowcase() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<Tab>("claude");
  const { data: samples, fetching, refetchHeygenStatus } = useSamplesData();

  return (
    <section id="ai-showcase" className="py-20 md:py-28 px-4 md:px-6 bg-background">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-4">
            {t("aiShowcase.eyebrow")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            {t("aiShowcase.title.1")}{" "}
            <span className="text-gradient-orange">{t("aiShowcase.title.2")}</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("aiShowcase.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 mb-8">
          {TABS.map(({ id, icon: Icon, labelKey }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(labelKey)}
            </button>
          ))}
        </div>

        <div className="rounded-3xl border border-border bg-card/40 overflow-hidden min-h-[420px]">
          {activeTab === "claude"    && <ClaudeTab />}
          {activeTab === "images"    && <ImagesTab samples={samples} fetching={fetching} />}
          {activeTab === "video"     && <VideoTab samples={samples} fetching={fetching} onRefresh={refetchHeygenStatus} />}
          {activeTab === "voice"     && <VoiceTab samples={samples} fetching={fetching} />}
          {activeTab === "bilingual" && <BilingualTab samples={samples} />}
        </div>
      </div>
    </section>
  );
}

/* ─── Tab 1: Live Claude Ad Writer ─────────────────────────────────── */

function ClaudeTab() {
  const { t } = useI18n();
  const [product, setProduct] = useState("");
  const [goal, setGoal] = useState("leads");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BilingualResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    if (!product.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await generateShowcaseAd(product.trim(), goal));
    } catch (e) {
      setError((e as Error).message ?? t("aiShowcase.claude.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 md:p-10">
      {!result ? (
        <div className="max-w-xl mx-auto space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground/80">
              {t("aiShowcase.claude.label")}
            </label>
            <Textarea
              placeholder={t("aiShowcase.claude.placeholder")}
              className="resize-none min-h-[100px] bg-background/60 text-sm"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              maxLength={300}
              disabled={loading}
            />
            <p className="text-right text-xs text-muted-foreground mt-1">{product.length}/300</p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-muted-foreground uppercase tracking-wide">
              {t("aiShowcase.claude.goal")}
            </label>
            <Select value={goal} onValueChange={setGoal} disabled={loading}>
              <SelectTrigger className="bg-background/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leads">{t("aiShowcase.claude.goal.leads")}</SelectItem>
                <SelectItem value="sales">{t("aiShowcase.claude.goal.sales")}</SelectItem>
                <SelectItem value="awareness">{t("aiShowcase.claude.goal.awareness")}</SelectItem>
                <SelectItem value="traffic">{t("aiShowcase.claude.goal.traffic")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button className="w-full glow-orange" disabled={!product.trim() || loading} onClick={handleGenerate}>
            {loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("aiShowcase.claude.generating")}</>
              : <><Wand2 className="w-4 h-4 mr-2" />{t("aiShowcase.claude.generate")}</>}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t("aiShowcase.claude.hint")}</p>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <AdCopyCard lang="EN" copy={result.en} />
            <AdCopyCard lang="ES" copy={result.es} />
          </div>
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => { setResult(null); setProduct(""); }}>
              <RefreshCw className="w-3.5 h-3.5 mr-2" />{t("aiShowcase.claude.tryAnother")}
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">{t("aiShowcase.claude.hint")}</p>
        </div>
      )}
    </div>
  );
}

function AdCopyCard({ lang, copy }: { lang: string; copy: AdCopy }) {
  return (
    <div className="rounded-2xl border border-border bg-background/60 p-5 space-y-3">
      <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-2 py-0.5 rounded-full">
        {lang}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Headline</p>
        <p className="font-semibold text-sm leading-snug">{copy.headline}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Body</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
      </div>
      <div className="pt-1 border-t border-border/50">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
          {copy.cta} <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
}

/* ─── Shared helpers ─────────────────────────────────────────────────── */

function BulletList({ keys }: { keys: string[] }) {
  const { t } = useI18n();
  return (
    <ul className="mt-4 space-y-2">
      {keys.map((k) => (
        <li key={k} className="flex items-start gap-2.5 text-sm text-muted-foreground">
          <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <span>{t(k)}</span>
        </li>
      ))}
    </ul>
  );
}

function ShowcaseCta() {
  const { t } = useI18n();
  function scrollToPricing() {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  }
  return (
    <div className="mt-6 flex justify-center">
      <Button onClick={scrollToPricing} className="glow-orange rounded-full px-6">
        {t("aiShowcase.cta")}
      </Button>
    </div>
  );
}

/* ─── Tab 2: Recraft Images ─────────────────────────────────────────── */

function ImagesTab({ samples, fetching }: { samples: ShowcaseMeta; fetching: boolean }) {
  const { t } = useI18n();
  const hasImages = samples.images.length >= 2;

  return (
    <div className="p-6 md:p-10">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-1">{t("aiShowcase.images.provider")}</p>
        <h3 className="text-lg md:text-xl font-bold">{t("aiShowcase.images.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t("aiShowcase.images.subtitle")}</p>
      </div>
      <div className="max-w-2xl mx-auto">
        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground/40" /></div>
        ) : hasImages ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {samples.images.slice(0, 2).map((url, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-border aspect-square bg-muted/20">
                <img src={url} alt={`AI ad creative ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-2xl border border-dashed border-border aspect-square bg-muted/10 flex flex-col items-center justify-center gap-3 text-center p-6">
                <Image className="w-10 h-10 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t("aiShowcase.images.placeholder")}</p>
                <p className="text-xs text-muted-foreground/60">{t("aiShowcase.images.runScript")}</p>
              </div>
            ))}
          </div>
        )}
        <BulletList keys={[
          "aiShowcase.images.bullet.1",
          "aiShowcase.images.bullet.2",
          "aiShowcase.images.bullet.3",
          "aiShowcase.images.bullet.4",
          "aiShowcase.images.bullet.5",
          "aiShowcase.images.bullet.6",
        ]} />
        <ShowcaseCta />
      </div>
    </div>
  );
}

/* ─── Tab 3: HeyGen Avatar Video ────────────────────────────────────── */

function VideoTab({ samples, fetching, onRefresh }: { samples: ShowcaseMeta; fetching: boolean; onRefresh: () => void }) {
  const { t } = useI18n();
  const [refreshing, setRefreshing] = useState(false);
  const isProcessing = !samples.heygenVideoUrl && samples.heygenVideoId;

  async function handleRefresh() {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  }

  return (
    <div className="p-6 md:p-10">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-1">{t("aiShowcase.video.provider")}</p>
        <h3 className="text-lg md:text-xl font-bold">{t("aiShowcase.video.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t("aiShowcase.video.subtitle")}</p>
      </div>
      <div className="max-w-lg mx-auto">
        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground/40" /></div>
        ) : samples.heygenVideoUrl ? (
          <div className="rounded-2xl overflow-hidden border border-border aspect-video bg-black">
            <video src={samples.heygenVideoUrl} controls className="w-full h-full object-contain" />
          </div>
        ) : isProcessing ? (
          <div className="rounded-2xl border border-border border-dashed aspect-video bg-muted/10 flex flex-col items-center justify-center gap-3 text-center p-8">
            <Loader2 className="w-10 h-10 text-primary/40 animate-spin" />
            <p className="text-sm font-medium">{t("aiShowcase.video.processing")}</p>
            <p className="text-xs text-muted-foreground">{t("aiShowcase.video.processingNote")}</p>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {t("aiShowcase.video.checkStatus")}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border aspect-video bg-muted/10 flex flex-col items-center justify-center gap-3 text-center p-8">
            <Video className="w-12 h-12 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{t("aiShowcase.video.placeholder")}</p>
            <p className="text-xs text-muted-foreground/60">{t("aiShowcase.video.runScript")}</p>
          </div>
        )}
        <BulletList keys={[
          "aiShowcase.video.bullet.1",
          "aiShowcase.video.bullet.2",
          "aiShowcase.video.bullet.3",
          "aiShowcase.video.bullet.4",
          "aiShowcase.video.bullet.5",
        ]} />
        <ShowcaseCta />
      </div>
    </div>
  );
}

/* ─── Tab 4: ElevenLabs Voiceover ───────────────────────────────────── */

function VoiceTab({ samples, fetching }: { samples: ShowcaseMeta; fetching: boolean }) {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    playing ? el.pause() : el.play();
    setPlaying(!playing);
  }

  return (
    <div className="p-6 md:p-10">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-1">{t("aiShowcase.voice.provider")}</p>
        <h3 className="text-lg md:text-xl font-bold">{t("aiShowcase.voice.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t("aiShowcase.voice.subtitle")}</p>
      </div>
      <div className="max-w-lg mx-auto space-y-5">
        <div className="rounded-2xl border border-border bg-background/60 p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">{t("aiShowcase.voice.script")}</p>
          <p className="text-sm leading-relaxed italic text-foreground/80">
            "{samples.adCopy.en.body}"
          </p>
        </div>
        {fetching ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground/40" /></div>
        ) : samples.audioUrl ? (
          <div className="rounded-2xl border border-border bg-background/60 p-5 flex items-center gap-4">
            <audio
              ref={audioRef}
              src={samples.audioUrl}
              onTimeUpdate={(e) => {
                const el = e.currentTarget;
                setProgress(el.duration ? el.currentTime / el.duration : 0);
              }}
              onEnded={() => setPlaying(false)}
            />
            <button
              type="button"
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0 hover:opacity-90 transition"
            >
              {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
            </button>
            <div className="flex-1">
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress * 100}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">ElevenLabs · Sarah · multilingual_v2</p>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-muted/10 p-5 flex flex-col items-center gap-3 text-center">
            <Volume2 className="w-10 h-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{t("aiShowcase.voice.placeholder")}</p>
            <p className="text-xs text-muted-foreground/60">{t("aiShowcase.voice.runScript")}</p>
          </div>
        )}
        <BulletList keys={[
          "aiShowcase.voice.bullet.1",
          "aiShowcase.voice.bullet.2",
          "aiShowcase.voice.bullet.3",
          "aiShowcase.voice.bullet.4",
        ]} />
        <ShowcaseCta />
      </div>
    </div>
  );
}

/* ─── Tab 5: EN vs ES Comparison ────────────────────────────────────── */

function BilingualTab({ samples }: { samples: ShowcaseMeta }) {
  const { t } = useI18n();
  const { en, es } = samples.adCopy;

  return (
    <div className="p-6 md:p-10">
      <div className="text-center mb-8">
        <h3 className="text-lg md:text-xl font-bold mb-2">{t("aiShowcase.bilingual.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("aiShowcase.bilingual.subtitle")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        <div className="rounded-2xl border border-border bg-background/60 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🇺🇸</span>
            <span className="text-sm font-bold uppercase tracking-wider">{t("aiShowcase.bilingual.en")}</span>
          </div>
          <BilingualField label="Headline" value={en.headline} />
          <BilingualField label="Body" value={en.body} />
          <BilingualCta value={en.cta} />
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🇪🇸</span>
            <span className="text-sm font-bold uppercase tracking-wider">{t("aiShowcase.bilingual.es")}</span>
          </div>
          <BilingualField label="Titular" value={es.headline} />
          <BilingualField label="Texto" value={es.body} />
          <BilingualCta value={es.cta} />
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-6">{t("aiShowcase.bilingual.note")}</p>
      <div className="max-w-2xl mx-auto">
        <BulletList keys={[
          "aiShowcase.bilingual.bullet.1",
          "aiShowcase.bilingual.bullet.2",
          "aiShowcase.bilingual.bullet.3",
          "aiShowcase.bilingual.bullet.4",
        ]} />
        <ShowcaseCta />
      </div>
    </div>
  );
}

function BilingualField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">{label}</p>
      <p className="text-sm leading-relaxed">{value}</p>
    </div>
  );
}

function BilingualCta({ value }: { value: string }) {
  return (
    <div className="pt-2 border-t border-border/50">
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
        {value} <ChevronRight className="w-3 h-3" />
      </span>
    </div>
  );
}
